import {useBoardCardHeadingLevel} from '@github-ui/board-core'
import {AT_COPILOT_VALUE} from '@github-ui/filter-core/providers'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {IssueStateIcons, PullRequestStateIcons} from '@github-ui/issue-pr-state/Icons'
import {RepositoryLabelToken} from '@github-ui/label-token/RepositoryLabelToken'
import {Link as RouterLink} from '@github-ui/react-router'
import {IssueTypeToken} from '@github-ui/issue-type-token'
import {IssueFieldSingleSelectValueToken} from '@github-ui/issue-metadata/IssueFieldSingleSelectValueToken'
import {addIssueFieldToQuery, createReplaceIssueFieldQuery} from '@github-ui/issue-url-helper'
import {
  addAssigneeFilterToQuery,
  addAuthorFilterToQuery,
  addDateFilterToQuery,
  addLabelFilterToQuery,
  addMilestoneFilterToQuery,
  addParentIssueFilterToQuery,
  addRepoFilterToQuery,
  addTypeFilterToQuery,
} from '@github-ui/shared-dashboard-surface/helpers/query-filters'
import {ProgressCircle} from '@github-ui/progress-circle'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {MilestoneIcon} from '@primer/octicons-react'
import {
  ActionList,
  AnchoredOverlay,
  AvatarStack,
  Button,
  Link as PrimerLink,
  RelativeTime,
  Token,
  Tooltip,
  VisuallyHidden,
} from '@primer/react'
import {clsx} from 'clsx'
import {type MouseEvent, type ReactNode, useCallback, useRef, useState} from 'react'

import type {FieldDefinition, ItemType} from '../../saved-view-options/fields'
import type {
  BoardPayloadAuthor,
  BoardPayloadAssignee,
  BoardPayloadIssueItem,
  BoardPayloadIssueType,
  BoardPayloadItem,
  BoardPayloadLabel,
  BoardPayloadMilestone,
  BoardPayloadParentIssue,
  BoardPayloadRepository,
  BoardPayloadSelectOption,
  BoardPayloadSubIssuesProgress,
} from './board-payload'
import {RepositoryIcon} from './RepositoryIcon'
import {type CardIssueFields, resolveVisibleCardFields} from './visible-card-fields'
import styles from './BoardCard.module.css'

/** What a host needs to open a card's item somewhere other than the item's own page. */
export interface BoardItemIdentifier {
  owner: string
  repo: string
  number: number
  globalId: string
  permalink: string
  isAccountIssue?: boolean
}

/**
 * How a host narrows its board by a card's tokens, the way the filter bar would. Every token on
 * the card is a button that filters, as in Memex and the issue lists.
 */
export interface BoardFilter {
  /** The board's current query, which a token adds its own filter to. */
  query: string
  /** Shows the board for a query in place. */
  apply: (query: string) => void
}

/**
 * Icons come from `@github-ui/issue-pr-state`, so every surface that shows issue/PR state
 * agrees on the glyph, colour, and accessible name.
 */
function StateIcon({item}: {item: BoardPayloadItem}) {
  if (item.type === 'issue') {
    return <IssueStateIcon state={item.state} stateReason={item.stateReason} />
  }

  switch (item.state) {
    case 'draft':
      return <PullRequestStateIcons.DRAFT size={12} />
    case 'merged':
      return <PullRequestStateIcons.MERGED size={12} />
    case 'closed':
      return <PullRequestStateIcons.CLOSED size={12} />
    case 'queued':
      return <PullRequestStateIcons.QUEUED size={12} />
    case 'archived':
      return <PullRequestStateIcons.ARCHIVED size={12} />
    default:
      return <PullRequestStateIcons.OPEN size={12} />
  }
}

function IssueStateIcon({
  state,
  stateReason,
  size = 12,
}: {
  state: BoardPayloadIssueItem['state']
  stateReason: BoardPayloadIssueItem['stateReason']
  size?: number
}) {
  if (state === 'open') return <IssueStateIcons.OPEN size={size} />
  if (stateReason === 'not_planned') return <IssueStateIcons.NOT_PLANNED size={size} />
  if (stateReason === 'duplicate') return <IssueStateIcons.DUPLICATE size={size} />
  return <IssueStateIcons.COMPLETED size={size} />
}

/** The same wording `IssueStateIcon` gives as its accessible name. */
function issueStateLabel(
  state: BoardPayloadIssueItem['state'],
  stateReason: BoardPayloadIssueItem['stateReason'],
): string {
  if (state === 'open') return 'Open'
  if (stateReason === 'not_planned') return 'Not planned'
  if (stateReason === 'duplicate') return 'Duplicate'
  return 'Completed'
}

/**
 * One card, drawn from a payload item.
 *
 * `visibleFieldIdentifiers` names the attributable fields the card should render, keyed by
 * the same field identifier the payload uses (`label`, `assignee`, `author`, `milestone`,
 * `repository`, `issue_type`, `field.<id>`). `fieldDefinitions` says which custom
 * fields are single- or multi-selects; without it, no custom field is drawn.
 */
export function BoardCard({
  item,
  visibleFieldIdentifiers,
  fieldDefinitions,
  onItemSelect,
  filter,
}: {
  item: BoardPayloadItem
  visibleFieldIdentifiers: ReadonlySet<string>
  fieldDefinitions?: readonly FieldDefinition[]
  /**
   * Handler that is called when the title is clicked, which returns whether it opened the item.
   * Returning `false`, or a modified click, follows the title link instead.
   */
  onItemSelect?: (itemType: ItemType, item: BoardItemIdentifier, returnFocusElement: HTMLElement) => boolean
  filter: BoardFilter
}) {
  const {
    labels,
    assignees,
    author,
    milestone,
    repository,
    issueType,
    issueFields,
    parentIssue,
    subIssuesProgress,
    created,
    updated,
    closed,
  } = resolveVisibleCardFields(item, visibleFieldIdentifiers, fieldDefinitions)
  const itemUrl = `/${item.repoNameWithOwner}/${item.type === 'issue' ? 'issues' : 'pull'}/${item.number}`
  const TitleHeading = `h${useBoardCardHeadingLevel()}` as const

  const handleTitleClick = onItemSelect
    ? (event: MouseEvent<HTMLAnchorElement>) => {
        if (event.defaultPrevented || !isPlainLeftClick(event)) return
        // The clicked anchor rather than `document.activeElement`, which a click does not move in Safari.
        if (onItemSelect(item.type, toItemIdentifier(item), event.currentTarget)) event.preventDefault()
      }
    : undefined

  return (
    <div className={styles.card}>
      <div className={styles.reference}>
        <span className={styles.referenceText}>
          <StateIcon item={item} />
          {`${item.repoNameWithOwner} #${item.number}`}
        </span>
        <Assignees assignees={assignees} filter={filter} />
      </div>

      <TitleHeading className={styles.titleHeading}>
        <PrimerLink as={RouterLink} to={itemUrl} className={styles.title} onClick={handleTitleClick}>
          {item.title}
        </PrimerLink>
      </TitleHeading>

      <Fields
        item={item}
        issueType={issueType}
        issueFields={issueFields}
        parentIssue={parentIssue}
        subIssuesProgress={subIssuesProgress}
        created={created}
        updated={updated}
        closed={closed}
        repository={repository}
        milestone={milestone}
        labels={labels}
        author={author}
        filter={filter}
      />
    </div>
  )
}

/** The author, as a button that filters the board by them. */
function AuthorToken({author, filter}: {author: BoardPayloadAuthor; filter: BoardFilter}) {
  const {login, avatarUrl, isCopilot} = author
  return (
    <FilterButtonToken
      name={`Author: ${login}`}
      leadingVisual={() => <GitHubAvatar alt="" src={avatarUrl} size={14} />}
      text={login}
      onClick={() => filter.apply(addAuthorFilterToQuery(filter.query, isCopilot ? AT_COPILOT_VALUE : login))}
    />
  )
}

/** The repository, as a button that filters the board by it. */
function RepositoryToken({repository, filter}: {repository: BoardPayloadRepository; filter: BoardFilter}) {
  const {nameWithOwner, repositoryStatus} = repository
  const status = REPOSITORY_STATUS_NAMES[repositoryStatus]
  return (
    <FilterButtonToken
      name={`Repository: ${nameWithOwner}${status ? ` (${status})` : ''}`}
      leadingVisual={() => <RepositoryIcon size={14} status={repositoryStatus} decorative />}
      text={nameWithOwner}
      onClick={() => filter.apply(addRepoFilterToQuery(filter.query, nameWithOwner))}
    />
  )
}

// Names the status the icon shows, so it is not conveyed by icon shape alone.
const REPOSITORY_STATUS_NAMES: Partial<Record<string, string>> = {
  public: 'public',
  private: 'private',
  forked: 'fork',
  archived: 'archived',
}

/** Meta, Ctrl, Shift and Alt clicks open a new tab, a new window or a download. */
function isPlainLeftClick(event: MouseEvent<HTMLAnchorElement>): boolean {
  // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- a modified link click, not a keyboard shortcut
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey
}

function toItemIdentifier(item: BoardPayloadItem): BoardItemIdentifier {
  const [owner = '', repo = ''] = item.repoNameWithOwner.split('/')
  return {
    owner,
    repo,
    number: item.number,
    globalId: item.globalId,
    permalink: item.permalink,
    ...(item.type === 'issue' && item.isAccountIssue ? {isAccountIssue: true} : {}),
  }
}

/** Each avatar is a button that filters the board by its assignee. */
function Assignees({assignees, filter}: {assignees: readonly BoardPayloadAssignee[]; filter: BoardFilter}) {
  if (assignees.length === 0) return null

  return (
    <span className={styles.assignees}>
      {/* 24px avatars so each target meets WCAG 2.5.8 minimum. The "equivalent control"
          exception doesn't apply on the board view — no other 24×24 control on the page
          filters by the assignee. */}
      <AvatarStack alignRight size={24}>
        {assignees.map(assignee => (
          <Assignee key={assignee.login} assignee={assignee} filter={filter} />
        ))}
      </AvatarStack>
    </span>
  )
}

function Assignee({
  assignee,
  filter,
  className,
}: {
  assignee: BoardPayloadAssignee
  filter: BoardFilter
  /** Injected by `AvatarStack` to apply its overlap and expansion styles. */
  className?: string
}) {
  const {login} = assignee
  return (
    <span className={clsx(className, styles.assigneeItem)}>
      <Tooltip text={`Assignee: ${login}`} type="label" direction="n">
        <button
          type="button"
          className={styles.assigneeButton}
          onClick={() =>
            filter.apply(addAssigneeFilterToQuery(filter.query, assignee.isCopilot ? AT_COPILOT_VALUE : login))
          }
        >
          <GitHubAvatar alt="" src={assignee.avatarUrl} size={24} />
        </button>
      </Tooltip>
    </span>
  )
}

/**
 * How many labels a card names before the rest roll into the overflow.
 *
 * Three, where the issues index caps nothing and lets every label it fetched run inline. A
 * card is a fifth of a row's width, so a row's answer would be several lines of tokens on
 * one; the count is what a card has instead of the room.
 */
const MAX_INLINE_LABELS = 3

/**
 * How many of the collapsed names the count carries in its tooltip before it stops naming
 * them and counts the remainder. An issue can hold dozens of labels, and a description that
 * grows with every one of them is read out in full on focus.
 */
const MAX_NAMED_OVERFLOW_LABELS = 3

/**
 * The card's fields in one row: issue type, parent issue, sub-issues progress, custom
 * single-selects, then multi-selects, the first few labels, a count standing for the rest of the
 * labels, author, repository, milestone, and the created, updated and closed dates.
 *
 * The count opens a list of the labels it stands for. A few of their names are on the count as
 * well, so hovering reads them without opening anything.
 */
function Fields({
  item,
  issueType,
  issueFields,
  parentIssue,
  subIssuesProgress,
  created,
  updated,
  closed,
  repository,
  milestone,
  labels,
  author,
  filter,
}: {
  item: BoardPayloadItem
  issueType: BoardPayloadIssueType | null
  issueFields: CardIssueFields
  parentIssue: BoardPayloadParentIssue | null
  subIssuesProgress: BoardPayloadSubIssuesProgress | null
  created: string | null
  updated: string | null
  closed: string | null
  repository: BoardPayloadRepository | null
  milestone: BoardPayloadMilestone | null
  labels: readonly BoardPayloadLabel[]
  author: BoardPayloadAuthor | null
  filter: BoardFilter
}) {
  if (
    issueFields.singleSelect.length === 0 &&
    issueFields.multiSelect.length === 0 &&
    labels.length === 0 &&
    author === null &&
    issueType === null &&
    parentIssue === null &&
    subIssuesProgress === null &&
    repository === null &&
    milestone === null &&
    created === null &&
    updated === null &&
    closed === null
  ) {
    return null
  }

  const inline = labels.slice(0, MAX_INLINE_LABELS)
  const overflow = labels.slice(MAX_INLINE_LABELS)

  return (
    <div className={styles.fields}>
      {issueType === null ? null : <IssueType issueType={issueType} filter={filter} />}
      {parentIssue === null ? null : <ParentIssueToken parentIssue={parentIssue} filter={filter} />}
      {subIssuesProgress === null ? null : (
        <SubIssuesProgressToken progress={subIssuesProgress} item={item} filter={filter} />
      )}
      {issueFields.singleSelect.map(({identifier, fieldName, option}) => (
        <SelectOptionToken key={identifier} fieldName={fieldName} option={option} filter={filter} />
      ))}
      {issueFields.multiSelect.flatMap(({identifier, fieldName, options}) =>
        options.map(option => (
          <SelectOptionToken key={`${identifier}:${option.id}`} fieldName={fieldName} option={option} filter={filter} />
        )),
      )}
      {inline.map(label => (
        <Label key={label.id} label={label} filter={filter} />
      ))}
      {overflow.length === 0 ? null : <LabelOverflow labels={overflow} filter={filter} />}
      {author === null ? null : <AuthorToken author={author} filter={filter} />}
      {repository === null ? null : <RepositoryToken repository={repository} filter={filter} />}
      {milestone === null ? null : <MilestoneToken milestone={milestone} filter={filter} />}
      {created === null ? null : <DateToken name="Created" qualifier="created" datetime={created} filter={filter} />}
      {updated === null ? null : <DateToken name="Updated" qualifier="updated" datetime={updated} filter={filter} />}
      {closed === null ? null : <DateToken name="Closed" qualifier="closed" datetime={closed} filter={filter} />}
    </div>
  )
}

/**
 * A date, like Memex's "Created: Oct 12, 2026". The server renders the UTC date and the browser
 * swaps in the viewer's local one. It is a button that, as in Memex, narrows the board to that
 * UTC day, and its tooltip adds the time, e.g. "Created: Oct 12, 2026, 1:11 PM PDT".
 */
function DateToken({
  name,
  qualifier,
  datetime,
  filter,
}: {
  name: string
  qualifier: 'created' | 'updated' | 'closed'
  datetime: string
  filter: BoardFilter
}) {
  const label = (
    <>
      {name}:{' '}
      <RelativeTime
        datetime={datetime}
        format="datetime"
        weekday={OMIT_DATE_PART}
        month="short"
        day="numeric"
        year="numeric"
        noTitle
      />
    </>
  )
  const date = new Date(datetime)
  const {isBrowser} = useRuntimeEnvironment()
  if (isNaN(date.getTime())) return <Token text={label} />

  const query = addDateFilterToQuery(filter.query, qualifier, date.toISOString().slice(0, 10))
  // The server can't know the viewer's time zone, so the server render and hydration use UTC.
  const time = (isBrowser ? localDateTimeFormat : utcDateTimeFormat).format(date)
  return <FilterButtonToken name={`${name}: ${time}`} text={label} onClick={() => filter.apply(query)} />
}

const dateTimeOptions: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZoneName: 'short',
}
const localDateTimeFormat = new Intl.DateTimeFormat('en-US', dateTimeOptions)
const utcDateTimeFormat = new Intl.DateTimeFormat('en-US', {...dateTimeOptions, timeZone: 'UTC'})

// relative-time adds a weekday to `format="datetime"` unless given ""; Primer's types don't allow "".
const OMIT_DATE_PART = '' as unknown as undefined

/** The issue type, as a button that filters the board by it. */
function IssueType({issueType, filter}: {issueType: BoardPayloadIssueType; filter: BoardFilter}) {
  const {name} = issueType

  return (
    <IssueTypeToken
      name={name}
      color={issueType.color}
      onSelect={() => filter.apply(addTypeFilterToQuery(filter.query, name))}
      getTooltipText={() => `Type: ${name}`}
      tooltipDirection="n"
      size="small"
    />
  )
}

/** A custom select option, as a button that filters the board by it, named "Priority: High". */
function SelectOptionToken({
  fieldName,
  option,
  filter,
}: {
  fieldName: string
  option: BoardPayloadSelectOption
  filter: BoardFilter
}) {
  const query = filter.query.trim()
    ? (createReplaceIssueFieldQuery(filter.query, fieldName, option.name) ?? filter.query)
    : addIssueFieldToQuery('', fieldName, option.name)
  return (
    <Tooltip text={`${fieldName}: ${option.name}`} type="label" direction="n" className={styles.fieldTooltip}>
      <IssueFieldSingleSelectValueToken
        name={option.name}
        color={option.color}
        getTooltipText={() => undefined}
        as="button"
        type="button"
        onClick={() => filter.apply(query)}
      />
    </Tooltip>
  )
}

/**
 * A token that narrows the board in place. A button rather than a link, matching Memex and the
 * issue lists' click-to-filter. As in Memex, its tooltip is its name and says what the token
 * is, e.g. "Priority: High".
 */
function FilterButtonToken({
  name,
  onClick,
  ...props
}: {
  name: string
  text: ReactNode
  onClick: () => void
  leadingVisual?: () => ReactNode
}) {
  return (
    <Tooltip text={name} type="label" direction="n" className={styles.fieldTooltip}>
      <Token {...props} as="button" type="button" onClick={onClick} />
    </Tooltip>
  )
}

/**
 * The parent issue, drawn as Memex draws it on a card: its state, title and number on a plain
 * token, with the issue's hovercard. A button that, as in Memex, narrows the board to the
 * parent's sub-issues.
 */
function ParentIssueToken({parentIssue, filter}: {parentIssue: BoardPayloadParentIssue; filter: BoardFilter}) {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const {titleHtml, number, state, stateReason, url, updatedAt} = parentIssue
  const repoNameWithOwner = repoNameWithOwnerFromIssueUrl(url)
  const text = (
    <>
      <VisuallyHidden>Parent issue:</VisuallyHidden> <VerifiedHTMLText as="span" html={titleHtml} /> #{number}
      <VisuallyHidden>({issueStateLabel(state, stateReason)})</VisuallyHidden>
    </>
  )
  // The glyph is hidden so the name starts with "Parent issue:" as in Memex; the state is read at the end instead.
  const leadingVisual = () => (
    <span aria-hidden="true" className={styles.parentIssueState}>
      <IssueStateIcon state={state} stateReason={stateReason} size={14} />
    </span>
  )

  // Without the parent's repository there is no filter to apply, so the token is only a label.
  if (repoNameWithOwner === null) {
    return <Token className={styles.parentIssueToken} leadingVisual={leadingVisual} text={text} />
  }

  return (
    <Token
      as="button"
      type="button"
      className={styles.parentIssueToken}
      leadingVisual={leadingVisual}
      text={text}
      onClick={() => filter.apply(addParentIssueFilterToQuery(filter.query, repoNameWithOwner, number))}
      {...getHovercardAttributesForType('issue', {url: `${url}/hovercard`, cache: updatedAt})}
    />
  )
}

/** `owner/repo` from an issue URL such as `https://github.com/owner/repo/issues/42`. */
function repoNameWithOwnerFromIssueUrl(url: string): string | null {
  try {
    const [, owner, repo, kind] = new URL(url, 'https://github.com').pathname.split('/')
    return owner && repo && kind === 'issues' ? `${owner}/${repo}` : null
  } catch {
    return null
  }
}

/**
 * The issue's sub-issues progress, drawn like the issue lists' "1 / 4" token. A button that, as
 * in Memex, filters the board to the issue's sub-issues.
 */
function SubIssuesProgressToken({
  progress,
  item,
  filter,
}: {
  progress: BoardPayloadSubIssuesProgress
  item: BoardPayloadItem
  filter: BoardFilter
}) {
  const {completed, total, percentCompleted} = progress
  return (
    <FilterButtonToken
      name={`Sub-issues progress: ${completed} / ${total} completed`}
      leadingVisual={() => (
        <ProgressCircle percentCompleted={percentCompleted} size={14} svgClassName={styles.subIssuesProgressCircle} />
      )}
      text={`${completed} / ${total}`}
      onClick={() => filter.apply(addParentIssueFilterToQuery(filter.query, item.repoNameWithOwner, item.number))}
    />
  )
}

/** The milestone, as a button that filters the board by it. */
function MilestoneToken({milestone, filter}: {milestone: BoardPayloadMilestone; filter: BoardFilter}) {
  return (
    <FilterButtonToken
      name={`Milestone: ${milestone.title}`}
      leadingVisual={() => <MilestoneIcon size={14} />}
      text={milestone.title}
      onClick={() => filter.apply(addMilestoneFilterToQuery(filter.query, milestone.title))}
    />
  )
}

/**
 * The labels a card had no room to name, as a count that opens them, each a button that
 * filters the board by that label, as in the issue lists.
 *
 * Opening on a click rather than on the hover: a list of controls has to be somewhere a
 * pointer can travel into and a keyboard can reach, and Primer's tooltip does not open that
 * way. Only the labels behind the count, since the ones in front of it are already named.
 */
function LabelOverflow({labels, filter}: {labels: readonly BoardPayloadLabel[]; filter: BoardFilter}) {
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLElement>(null)
  const onOpen = useCallback(() => setOpen(true), [])
  const onClose = useCallback(() => setOpen(false), [])

  return (
    <AnchoredOverlay
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      align="start"
      anchorRef={anchorRef}
      renderAnchor={anchorProps => (
        // Primer's tooltip owns the trigger ref, so the overlay is anchored through it.
        <Tooltip ref={anchorRef} direction="n" text={overflowSummary(labels)}>
          <Button
            {...anchorProps}
            // Every `aria-haspopup` value names a widget this overlay is not; `aria-expanded` alone
            // is what a disclosure reports.
            aria-haspopup={undefined}
            variant="invisible"
            size="small"
            className={styles.labelOverflowButton}
          >
            {`+${labels.length}`}
            <span className="sr-only">{labels.length === 1 ? ' more label' : ' more labels'}</span>
          </Button>
        </Tooltip>
      )}
    >
      <ActionList aria-label="More labels" className={styles.labelOverflowList}>
        {labels.map(label => (
          <ActionList.Item
            key={label.id}
            onSelect={() => {
              setOpen(false)
              filter.apply(addLabelFilterToQuery(filter.query, label.name))
            }}
          >
            <RepositoryLabelToken label={toTokenLabel(label)} />
          </ActionList.Item>
        ))}
      </ActionList>
    </AnchoredOverlay>
  )
}

/**
 * The names the count carries in its tooltip: the first few, then how many are left.
 */
function overflowSummary(labels: readonly BoardPayloadLabel[]): string {
  const named = labels.slice(0, MAX_NAMED_OVERFLOW_LABELS).map(label => label.name)
  const remaining = labels.length - named.length
  return remaining === 0 ? named.join(', ') : `${named.join(', ')} and ${remaining} more`
}

/** Label token, as a button that filters the board by the label, with a description tooltip. */
function Label({label, filter}: {label: BoardPayloadLabel; filter: BoardFilter}) {
  const description = label.description ?? ''
  return (
    <Tooltip
      text={description}
      type="description"
      direction="n"
      className={clsx(styles.fieldTooltip, !label.description && styles.tooltipHidden)}
    >
      <RepositoryLabelToken
        label={toTokenLabel(label)}
        as="button"
        type="button"
        onClick={() => filter.apply(addLabelFilterToQuery(filter.query, label.name))}
      />
    </Tooltip>
  )
}

/**
 * `nameHTML` is what carries emoji markup; the payload spells it `nameHtml` and the shared
 * token expects `nameHTML`, so it is renamed here until the payload aligns.
 */
function toTokenLabel(label: BoardPayloadLabel) {
  return {name: label.name, nameHTML: label.nameHtml, color: label.color}
}
